import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthContext';
import * as api from '../../../api';
import PageHeader from '../../../app/PageHeader';
import useApiRequest from '../../../hooks/useApiRequest';
import Avatar from '../../../components/primitives/Avatar';
import ConfirmPasswordDialog from '../../../components/feedback/ConfirmPasswordDialog';
import StatePanel from '../../../components/feedback/StatePanel';
import { fullName } from '../../../utils/names';
import ViewAsConfirmationDialog from '../components/ViewAsConfirmationDialog';
import '../AdminUsers.css';

function formFrom(account) {
  return {
    first_name: account.first_name,
    last_name: account.last_name,
    username: account.username,
    email: account.email,
    sponsor_org_id: String(account.sponsor_org.id),
    is_active: account.is_active,
  };
}

export default function SponsorAccountPage() {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  const [notice, setNotice] = useState('');
  const [viewingAs, setViewingAs] = useState(false);
  const [confirmingViewAs, setConfirmingViewAs] = useState(false);

  const viewAsSponsor = async () => {
    setViewingAs(true);
    setErrors({});
    try {
      await startImpersonation(userId);
      navigate('/', { state: { viewAsReturnTo: location.pathname } });
    } catch (error) {
      setErrors({ detail: error.message });
      setConfirmingViewAs(false);
      setViewingAs(false);
    }
  };

  const {
    data,
    setData,
    status,
    reload: load,
  } = useApiRequest(
    useCallback(async () => {
      const [account, organizations] = await Promise.all([
        api.getAdminSponsor(userId),
        api.getAdminSponsorOrganizations(),
      ]);
      return { account, organizations };
    }, [userId]),
    {
      skip: user?.account_type !== 'admin',
      onSuccess: ({ account }) => setForm(formFrom(account)),
    },
  );
  const account = data?.account;
  const organizations = data?.organizations || [];

  useEffect(() => {
    document.title = 'Sponsor account | Good Driver Incentive Program';
  }, []);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const cancel = () => {
    setForm(formFrom(account));
    setErrors({});
    setEditing(false);
  };

  // Deactivating locks the owner out, so the server asks for the admin's password.
  const save = async (event, currentPassword) => {
    event?.preventDefault();
    const clientErrors = {};
    if (!form.first_name.trim()) clientErrors.first_name = 'First name is required.';
    if (!form.last_name.trim()) clientErrors.last_name = 'Last name is required.';
    if (!/^[A-Za-z0-9._-]{3,30}$/.test(form.username))
      clientErrors.username = 'Use 3 to 30 letters, numbers, periods, dashes, or underscores.';
    if (!form.email.trim()) clientErrors.email = 'Email is required.';
    if (!form.sponsor_org_id) clientErrors.sponsor_org_id = 'Choose a sponsor organization.';
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    if (account.is_active && !form.is_active && currentPassword === undefined) {
      setConfirmingDeactivation(true);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const saved = await api.updateAdminSponsor(userId, {
        ...form,
        ...(currentPassword ? { current_password: currentPassword } : {}),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        username: form.username.trim(),
        email: form.email.trim(),
        sponsor_org_id: Number(form.sponsor_org_id),
      });
      setData((current) => ({ ...current, account: saved }));
      setForm(formFrom(saved));
      setEditing(false);
      setNotice('Sponsor account saved.');
      setConfirmingDeactivation(false);
    } catch (error) {
      // A wrong password is shown in the password dialog, which stays open.
      if (currentPassword !== undefined && (error.data?.current_password || error.status === 429))
        throw error;
      setConfirmingDeactivation(false);
      setErrors(error.data || { detail: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (user?.account_type !== 'admin' || status === 'forbidden')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          title="You don't have access to this page"
        >
          <p>Only administrators can view and edit sponsor accounts.</p>
        </StatePanel>
      </main>
    );
  if (status === 'loading')
    return (
      <main className="sponsor-detail-page">
        <p role="status">Loading sponsor account…</p>
        <section
          className="card sponsor-detail-card sponsor-detail-skeleton"
          aria-label="Loading the account"
        />
      </main>
    );
  if (status === 'error')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          tone="error"
          title="This account couldn't be loaded"
        >
          <p>Check your connection and try again.</p>
          <button type="button" onClick={load}>
            Try again
          </button>
        </StatePanel>
      </main>
    );
  if (status === 'not-found')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          title="That sponsor account doesn't exist"
        >
          <p>It may have been removed, or the link may be wrong.</p>
          <Link to="/users">Back to users</Link>
        </StatePanel>
      </main>
    );

  const name = fullName(account);
  return (
    <div className="sponsor-detail-page">
      <PageHeader
        title="Sponsor account"
        breadcrumb={
          <>
            <Link to="/users">Users</Link> / {name}
          </>
        }
      />
      <main className="sponsor-detail-content">
        <section className="card sponsor-detail-card">
          <div className="sponsor-detail-card-head">
            <div className="sponsor-detail-person">
              <Avatar className="sponsor-detail-avatar" name={name} />
              <div>
                <h2>{name}</h2>
                <p>
                  @{account.username} <span>Sponsor</span> ·{' '}
                  {account.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
            {!editing && (
              <div className="sponsor-detail-header-actions">
                <button
                  type="button"
                  onClick={() => setConfirmingViewAs(true)}
                  disabled={viewingAs || !account.is_active || account.view_as?.eligible === false}
                  aria-describedby={
                    !account.is_active || account.view_as?.eligible === false
                      ? 'sponsor-view-as-disabled'
                      : undefined
                  }
                >
                  {viewingAs ? 'Opening…' : 'View as sponsor'}
                </button>
                {(!account.is_active || account.view_as?.eligible === false) && (
                  <p className="view-as-disabled-reason" id="sponsor-view-as-disabled">
                    {!account.is_active
                      ? 'Inactive accounts cannot be viewed as.'
                      : account.view_as.detail}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setNotice('');
                    setEditing(true);
                  }}
                >
                  Edit account
                </button>
              </div>
            )}
          </div>
          {errors.detail && !editing && (
            <p className="banner banner-error sponsor-detail-banner" role="alert">
              {errors.detail}
            </p>
          )}
          {notice && (
            <p className="banner banner-success sponsor-detail-banner" role="status">
              {notice}
            </p>
          )}
          {!editing ? (
            <dl className="sponsor-detail-view">
              <div>
                <dt>Full name</dt>
                <dd>{name}</dd>
              </div>
              <div>
                <dt>Username</dt>
                <dd>@{account.username}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{account.email}</dd>
              </div>
              <div>
                <dt>Sponsor organization</dt>
                <dd>{account.sponsor_org.name}</dd>
              </div>
              <div>
                <dt>Account status</dt>
                <dd>{account.is_active ? 'Active' : 'Inactive'}</dd>
              </div>
              <div>
                <dt>Account type</dt>
                <dd>
                  Sponsor <small>Locked after account creation</small>
                </dd>
              </div>
            </dl>
          ) : (
            <form onSubmit={save} noValidate>
              {errors.detail && (
                <p className="banner banner-error sponsor-detail-banner" role="alert">
                  {errors.detail}
                </p>
              )}
              <div className="sponsor-detail-form">
                <label>
                  First name
                  <input
                    value={form.first_name}
                    onChange={(e) => update('first_name', e.target.value)}
                  />
                  {errors.first_name && <small>{errors.first_name}</small>}
                </label>
                <label>
                  Last name
                  <input
                    value={form.last_name}
                    onChange={(e) => update('last_name', e.target.value)}
                  />
                  {errors.last_name && <small>{errors.last_name}</small>}
                </label>
                <label>
                  Username
                  <input
                    value={form.username}
                    onChange={(e) => update('username', e.target.value)}
                  />
                  {errors.username && <small>{errors.username}</small>}
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                  />
                  {errors.email && <small>{errors.email}</small>}
                </label>
                <label>
                  Sponsor organization
                  <select
                    value={form.sponsor_org_id}
                    onChange={(e) => update('sponsor_org_id', e.target.value)}
                  >
                    {organizations.map((org) => (
                      <option value={org.id} key={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {errors.sponsor_org_id && <small>{errors.sponsor_org_id}</small>}
                </label>
                <label>
                  Account status
                  <select
                    value={String(form.is_active)}
                    onChange={(e) => update('is_active', e.target.value === 'true')}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                  <small>An inactive account cannot sign in.</small>
                </label>
                <div className="sponsor-detail-locked">
                  <span>Account type</span>
                  <strong>Sponsor</strong>
                  <small>Cannot be changed after account creation.</small>
                </div>
              </div>
              <div className="sponsor-detail-actions">
                <button type="button" onClick={cancel} disabled={saving}>
                  Cancel
                </button>
                <button className="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          )}
        </section>
      </main>
      {confirmingDeactivation && (
        <ConfirmPasswordDialog
          title="Deactivate this account?"
          message={`${name} won't be able to sign in until the account is made active again. Enter your password to confirm.`}
          confirmLabel="Deactivate account"
          onConfirm={(password) => save(null, password)}
          onCancel={() => setConfirmingDeactivation(false)}
        />
      )}
      {confirmingViewAs && (
        <ViewAsConfirmationDialog
          account={{
            name,
            username: account.username,
            role: 'sponsor',
            organization: account.sponsor_org?.name,
            isActive: account.is_active,
          }}
          administratorName={user.name || user.username || 'your administrator account'}
          busy={viewingAs}
          onConfirm={viewAsSponsor}
          onCancel={() => setConfirmingViewAs(false)}
        />
      )}
    </div>
  );
}
